import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantStore } from '@/stores/useTenantStore';
import { Building2, Home, CheckCircle2 } from 'lucide-react';

export const TenancySelector: React.FC = () => {
    const navigate = useNavigate();
    const { tenancies, selectedTenancyId, selectTenancy, fetchTenancies } = useTenantStore();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (tenancies.length === 0) {
            loadTenancies();
        }
    }, [tenancies.length]);

    const loadTenancies = async () => {
        setLoading(true);
        await fetchTenancies();
        setLoading(false);
    };

    const handleSelect = (tenancyId: string, propertyId: string) => {
        selectTenancy(tenancyId, propertyId);
        navigate('/tenant');
    };

    return (
        <div className="max-w-3xl mx-auto space-y-6 mt-12">
            <div className="text-center mb-10">
                <div className="inline-flex p-4 bg-primary/10 text-primary rounded-full mb-4">
                    <Building2 size={32} />
                </div>
                <h1 className="text-3xl font-bold text-light-text dark:text-dark-text mb-2">Select Your Unit</h1>
                <p className="text-light-subtle dark:text-dark-subtle">
                    You have multiple active tenancies. Please select the one you want to view right now.
                </p>
            </div>

            {loading ? (
                <p className="text-center text-light-subtle dark:text-dark-subtle py-8">Loading your tenancies...</p>
            ) : tenancies.length === 0 ? (
                <div className="text-center bg-light-card dark:bg-dark-card p-12 rounded-xl border border-light-border dark:border-dark-border">
                    <h3 className="text-lg font-medium text-light-text dark:text-dark-text mb-2">No active tenancies found</h3>
                    <p className="text-light-subtle dark:text-dark-subtle mb-6">You don't appear to have any active leases linked to your account.</p>
                    <button onClick={() => navigate('/')} className="btn-primary px-8">Return Home</button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {tenancies.map((t) => {
                        const isSelected = selectedTenancyId === t.tenancyId;
                        
                        return (
                            <button
                                key={t.tenancyId}
                                onClick={() => handleSelect(t.tenancyId, t.propertyId)}
                                className={`text-left p-6 rounded-xl border transition-all group relative overflow-hidden
                                    ${isSelected 
                                        ? 'border-primary bg-primary/5 dark:bg-primary/10 shadow-sm' 
                                        : 'border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card hover:border-primary/50'
                                    }`}
                            >
                                {isSelected && (
                                    <div className="absolute top-4 right-4 text-primary">
                                        <CheckCircle2 size={24} />
                                    </div>
                                )}
                                
                                <div className={`p-3 rounded-lg inline-block text-primary mb-4 ${isSelected ? 'bg-primary/10' : 'bg-light-bg dark:bg-dark-bg group-hover:bg-primary/10'}`}>
                                    <Home size={24} />
                                </div>
                                
                                <h3 className="text-xl font-bold text-light-text dark:text-dark-text mb-1">
                                    {t.unitName || 'Unit'}
                                </h3>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle mb-4">
                                    {t.propertyName || 'Property'}
                                </p>
                                
                                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                    {t.status || 'Active'}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
